import { supabase } from '../supabase';

async function readFunctionErrorBody(error) {
  if (!error?.context || typeof error.context.json !== 'function') {
    return null;
  }

  try {
    const body = await error.context.json();
    return body?.error || body?.details || null;
  } catch {
    return null;
  }
}

async function invokeBillingFunction(name, body) {
  const { data, error } = body
    ? await supabase.functions.invoke(name, { body })
    : await supabase.functions.invoke(name);

  if (error) {
    const details = data?.error || await readFunctionErrorBody(error);
    throw new Error(details || error.message || `Edge Function "${name}" failed`);
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data;
}

/**
 * Fetch Stripe prices for the hybrid billing catalog.
 * @returns {Promise<{ prices: Record<string, { month?: object, year?: object }>, warnings?: string[] }>}
 */
export async function getBillingPlans() {
  const data = await invokeBillingFunction('list-plans');
  return {
    prices: data?.prices ?? {},
    warnings: data?.warnings ?? [],
  };
}

/**
 * Create a Stripe Checkout session for a SmartWatcher catalog plan.
 * @param {{ productId: string, priceId: string, interval?: 'month' | 'year' }} params
 * @returns {Promise<{ url: string }>}
 */
export async function createCatalogCheckoutSession({ productId, priceId, interval, returnPath = '/billing' }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const data = await invokeBillingFunction('create-checkout-session', {
    productId,
    priceId,
    interval,
    origin,
    success_url: `${origin}${returnPath}?success=1`,
    cancel_url: `${origin}${returnPath}?cancel=1`,
  });

  if (!data?.url) throw new Error('No checkout URL returned');
  return { url: data.url };
}

/**
 * @deprecated Use createCatalogCheckoutSession for SmartWatcher plans.
 */
export async function createCheckoutSession(plan) {
  return createCatalogCheckoutSession({ productId: plan, priceId: plan });
}

/**
 * Preview switching the active subscription to another plan.
 * @param {{ productId: string, priceId: string, interval: 'month' | 'year' }} params
 * @returns {Promise<{ direction: 'upgrade' | 'downgrade', amountDue: number, currency: string, effectiveAt?: string | null, newPlan: string }>}
 */
export async function previewPlanChange({ productId, priceId, interval }) {
  return invokeBillingFunction('change-subscription-plan', {
    productId,
    priceId,
    interval,
    preview: true,
  });
}

/**
 * Apply a plan change on the active subscription.
 * Upgrades apply immediately with proration; downgrades are scheduled for period end.
 * @param {{ productId: string, priceId: string, interval: 'month' | 'year' }} params
 * @returns {Promise<{ ok: boolean, direction: 'upgrade' | 'downgrade', newPlan: string, effectiveAt?: string | null }>}
 */
export async function changePlan({ productId, priceId, interval }) {
  return invokeBillingFunction('change-subscription-plan', {
    productId,
    priceId,
    interval,
  });
}

/**
 * Cancel subscription at end of billing period.
 * User keeps access until current_period_end.
 * @returns {Promise<{ success: boolean }>}
 */
export async function cancelSubscription() {
  const { data, error } = await supabase.functions.invoke('cancel-subscription');

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { success: data?.success ?? true };
}

/**
 * Fetch payment history (Stripe invoices) for the current user.
 * Calls Supabase Edge Function list-invoices.
 * @returns {Promise<{ transactions: Array<{ id, date, description, amount, status }> }>}
 */
export async function getPaymentHistory() {
  const { data, error } = await supabase.functions.invoke('list-invoices');

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { transactions: data?.transactions ?? [] };
}
